import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { IContact, ImpersonationBroker, IPagedCollection, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';
import { FocusedLeadContact, toFocusedLeadContact } from './types';

/**
 * Fetches the first page of contacts for a given company via
 * `GET contact/byCompany/{id}`. Same endpoint the legacy
 * `LeadViewModel.getContacts()` uses, but exposed through react-query.
 *
 * Returns mapped `FocusedLeadContact[]` with `primary` left undefined; the
 * decision-maker flag is layered in by `withContacts` from the lead's
 * `decisionMakerIds` (the canonical source of truth).
 */
export function useLeadContacts(companyId: string | undefined): UseQueryResult<FocusedLeadContact[], Error> {
	const userSession = useUserSession();

	return useQuery<FocusedLeadContact[], Error>({
		queryKey: queryKeys.focusedView.contacts(companyId),
		enabled: !!companyId,
		queryFn: async () => {
			const result = await userSession.webServiceHelper.callWebServiceAsync<IPagedCollection<IContact>>(
				ImpersonationBroker.composeApiUrl({ urlPath: `contact/byCompany/${companyId}` }),
				'GET'
			);
			if (!result.success) {
				throw asApiError(result);
			}
			const seen = new Set<string>();
			return (result.value?.values ?? [])
				.filter(c => {
					const id = c.id ?? '';
					if (!id || seen.has(id)) return false;
					seen.add(id);
					return true;
				})
				.map(toFocusedLeadContact);
		},
	});
}
